import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, AlertCircle, Layers } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

export function BatchCreateDialog({
  open, onOpenChange,
  populations, stimuli,
  user, selectedClientId,
  onCreated,
  api,
}) {
  const [batchNamePrefix, setBatchNamePrefix] = useState('');
  const [batchPopulationId, setBatchPopulationId] = useState('');
  const [selectedStimuliIds, setSelectedStimuliIds] = useState([]);
  const [batchSampleSize, setBatchSampleSize] = useState('');
  const [batchCreating, setBatchCreating] = useState(false);
  const [batchFormTouched, setBatchFormTouched] = useState({ namePrefix: false, populationId: false, stimuli: false });

  const resetBatchForm = () => {
    setBatchNamePrefix(''); setBatchPopulationId(''); setSelectedStimuliIds([]); setBatchSampleSize('');
    setBatchFormTouched({ namePrefix: false, populationId: false, stimuli: false });
  };

  const toggleStimulusSelection = (stimulusId) => {
    setSelectedStimuliIds(prev => prev.includes(stimulusId) ? prev.filter(id => id !== stimulusId) : [...prev, stimulusId]);
  };

  const handleBatchCreate = async (e) => {
    e.preventDefault();
    if (user?.is_admin && (!selectedClientId || selectedClientId === 'all')) {
      toast.error('Please select a specific client before creating batch simulations');
      return;
    }
    if (selectedStimuliIds.length === 0) { toast.error('Please select at least one stimulus'); return; }
    setBatchCreating(true);
    try {
      const payload = {
        name_prefix: batchNamePrefix, population_id: batchPopulationId, stimulus_ids: selectedStimuliIds,
        sample_size: batchSampleSize ? parseInt(batchSampleSize) : null, config: { batch_size: 400, concurrency_limit: 15 },
      };
      const url = user?.is_admin && selectedClientId && selectedClientId !== 'all'
        ? `/simulations/batch?client_id=${selectedClientId}` : `/simulations/batch`;
      const response = await api.post(url, payload);
      toast.success(`Created ${response.data.simulations.length} simulations`);
      onOpenChange(false);
      resetBatchForm();
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create batch simulations');
    } finally {
      setBatchCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetBatchForm(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5" />Batch Create Simulations</DialogTitle>
          <DialogDescription>Run the same population against multiple stimuli at once</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleBatchCreate} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="batch-name">Name Prefix <span className="text-red-500">*</span></Label>
            <Input id="batch-name" value={batchNamePrefix} onChange={(e) => setBatchNamePrefix(e.target.value)}
              onBlur={() => setBatchFormTouched(prev => ({ ...prev, namePrefix: true }))}
              placeholder="e.g., Q4 Policy Analysis"
              className={cn(batchFormTouched.namePrefix && !batchNamePrefix && "border-red-300 focus-visible:ring-red-500")}
              data-testid="batch-name-input" />
            <p className="text-xs text-gray-500">Each simulation will be named: [prefix] - [stimulus name]</p>
            {batchFormTouched.namePrefix && !batchNamePrefix && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Name prefix is required</p>}
          </div>

          <div className="space-y-2">
            <Label>Population <span className="text-red-500">*</span></Label>
            <Select value={batchPopulationId} onValueChange={(v) => { setBatchPopulationId(v); setBatchFormTouched(prev => ({ ...prev, populationId: true })); }}>
              <SelectTrigger data-testid="batch-population-select" className={cn(batchFormTouched.populationId && !batchPopulationId && "border-red-300 focus:ring-red-500")} onBlur={() => setBatchFormTouched(prev => ({ ...prev, populationId: true }))}>
                <SelectValue placeholder="Select population" />
              </SelectTrigger>
              <SelectContent>{populations.map(pop => <SelectItem key={pop.id} value={pop.id}>{pop.name} ({pop.row_count} individuals)</SelectItem>)}</SelectContent>
            </Select>
            {batchFormTouched.populationId && !batchPopulationId && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Please select a population</p>}
          </div>

          <div className="space-y-2">
            <Label>Select Stimuli <span className="text-red-500">*</span> ({selectedStimuliIds.length} selected)</Label>
            <div className={cn("border rounded-lg max-h-48 overflow-y-auto", batchFormTouched.stimuli && selectedStimuliIds.length === 0 ? "border-red-300" : "border-gray-200")}>
              {stimuli.map(stim => (
                <div key={stim.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                  <Checkbox id={`stim-${stim.id}`} checked={selectedStimuliIds.includes(stim.id)} onCheckedChange={() => { toggleStimulusSelection(stim.id); setBatchFormTouched(prev => ({ ...prev, stimuli: true })); }} data-testid={`batch-stimulus-${stim.id}`} />
                  <label htmlFor={`stim-${stim.id}`} className="flex-1 cursor-pointer">
                    <p className="text-sm font-medium text-gray-900">{stim.name}</p>
                    <p className="text-xs text-gray-500 truncate">{stim.prompt_text}</p>
                  </label>
                </div>
              ))}
              {stimuli.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No stimuli available</p>}
            </div>
            {batchFormTouched.stimuli && selectedStimuliIds.length === 0 && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Please select at least one stimulus</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="batch-sample-size">Sample Size (Optional)</Label>
            <Input id="batch-sample-size" type="number" value={batchSampleSize} onChange={(e) => setBatchSampleSize(e.target.value)} placeholder="Leave empty for all" min={1} data-testid="batch-sample-size-input" />
          </div>

          <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
            <p className="text-xs text-blue-800"><strong>Batch Processing:</strong> {selectedStimuliIds.length} simulation{selectedStimuliIds.length !== 1 ? 's' : ''} will be created and can be run sequentially.</p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={batchCreating || !batchNamePrefix || !batchPopulationId || selectedStimuliIds.length === 0}
              onClick={() => setBatchFormTouched({ namePrefix: true, populationId: true, stimuli: true })}
              className={cn("bg-primary hover:bg-primary/90", (!batchNamePrefix || !batchPopulationId || selectedStimuliIds.length === 0) && "opacity-70")}
              data-testid="submit-batch-btn">
              {batchCreating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>) : (<><Layers className="w-4 h-4 mr-2" />Create {selectedStimuliIds.length} Simulation{selectedStimuliIds.length !== 1 ? 's' : ''}</>)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
